import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosCnss } from "../../../api/axios"; 
import { CNSS_PATHS } from "../../../api/endpoints";


// ── Helper de log ────────────────────────────────────────────────────────────
const logRequest = (label, url) => {
  if (process.env.NODE_ENV === "development") {
  }
};

// ════════════════════════════════════════════════════════════════════════════
//  CARTES
// ════════════════════════════════════════════════════════════════════════════

export const fetchAllCards = createAsyncThunk(
  "cnss/fetchAllCards",
  async (_, { rejectWithValue }) => {
    try {
      logRequest("GET all cards", CNSS_PATHS.ALL);
      const { data } = await axiosCnss.get(CNSS_PATHS.ALL);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur chargement cartes");
    }
  }
);

export const fetchCardById = createAsyncThunk(
  "cnss/fetchCardById",
  async (cardId, { rejectWithValue }) => {
    try {
      const path = CNSS_PATHS.BY_ID(cardId);
      logRequest("GET card by id", path);
      const { data } = await axiosCnss.get(path);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Carte introuvable");
    }
  }
);

export const createCardByPatient = createAsyncThunk(
  "cnss/createCardByPatient",
  async ({ userId, cardData }, { rejectWithValue }) => {
    try {
      const path = CNSS_PATHS.CREATE_BY_PATIENT(userId); // ✅
      const { data } = await axiosCnss.post(path, cardData);
      return data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data ?? { message: err?.message ?? "Erreur réseau" }
      );
    }
  }
);

export const fetchCardsByDoctorPatient = createAsyncThunk(
  "cnss/fetchCardsByDoctorPatient",
  async ({ doctorId, patientId }, { rejectWithValue }) => {
    try {
      const path = CNSS_PATHS.BY_DOCTOR_PATIENT(doctorId, patientId);
      logRequest("GET cards by doctor patient", path);

      const { data } = await axiosCnss.get(path);
      return data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ??
          "Erreur chargement carnet CNSS du patient"
      );
    }
  }
);

export const createCardByDoctor = createAsyncThunk(
  "cnss/createCardByDoctor",
  async ({ doctorId, patientId, cardData }, { rejectWithValue }) => {
    try {
      const path = CNSS_PATHS.CREATE_BY_DOCTOR(doctorId, patientId);
      logRequest("POST createByDoctor", path);
      const { data } = await axiosCnss.post(path, cardData);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur création carte");
    }
  }
);

export const updateCard = createAsyncThunk(
  "cnss/updateCard",
  async ({ cardId, verifierId, updateData }, { rejectWithValue }) => {
    try {
      const path = CNSS_PATHS.UPDATE(cardId, verifierId);
      logRequest("PUT updateCard", path);
      const { data } = await axiosCnss.put(path, updateData);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur mise à jour");
    }
  }
);

export const deleteCard = createAsyncThunk(
  "cnss/deleteCard",
  async (cardId, { rejectWithValue }) => {
    try {
      const path = CNSS_PATHS.DELETE(cardId);
      logRequest("DELETE card", path);
      await axiosCnss.delete(path);
      return cardId;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur suppression");
    }
  }
);

// ════════════════════════════════════════════════════════════════════════════
//  RECHERCHE & FILTRES
// ════════════════════════════════════════════════════════════════════════════

export const fetchStatistics = createAsyncThunk(
  "cnss/fetchStatistics",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosCnss.get(CNSS_PATHS.STATISTICS);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur statistiques");
    }
  }
);

export const fetchGlobalStats = createAsyncThunk(
  "cnss/fetchGlobalStats",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosCnss.get(CNSS_PATHS.STATISTICS_GLOBAL);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur stats globales");
    }
  }
);

export const fetchUnverifiedCards = createAsyncThunk(
  "cnss/fetchUnverifiedCards",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosCnss.get(CNSS_PATHS.UNVERIFIED);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur");
    }
  }
);

export const fetchExpiringCards = createAsyncThunk(
  "cnss/fetchExpiringCards",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosCnss.get(CNSS_PATHS.EXPIRING);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur");
    }
  }
);

export const fetchExpiredCards = createAsyncThunk(
  "cnss/fetchExpiredCards",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosCnss.get(CNSS_PATHS.EXPIRED);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur");
    }
  }
);

/**
 * Vérifier une carte par identifiant unique
 * → Gateway : GET /cnss/cards/verify?identifiant=xxx
 * → MS      : GET /api/cnss/cards/verify/check?identifiant=xxx (via buildUrl)
 */
export const verifyCardByIdentifiant = createAsyncThunk(
  "cnss/verifyCardByIdentifiant",
  async (identifiant, { rejectWithValue }) => {
    try {
      // On passe identifiant en param axios (pas dans l'URL)
      // pour éviter tout double-encodage
      logRequest("GET verify", `/verify?identifiant=${identifiant}`);
      const { data } = await axiosCnss.get("/verify", {
        params: { identifiant },
      });
      return data; // { card, isValid }
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message ?? "Identifiant invalide ou introuvable"
      );
    }
  }
);

export const searchByName = createAsyncThunk(
  "cnss/searchByName",
  async (nomPrenom, { rejectWithValue }) => {
    try {
      const { data } = await axiosCnss.get("/search/name", {
        params: { nomPrenom },
      });
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur recherche");
    }
  }
);

export const searchByCin = createAsyncThunk(
  "cnss/searchByCin",
  async (cin, { rejectWithValue }) => {
    try {
      const { data } = await axiosCnss.get("/search/cin", {
        params: { cin },
      });
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur recherche");
    }
  }
);

// ════════════════════════════════════════════════════════════════════════════
//  VUES PAR PATIENT / CRÉATEUR
// ════════════════════════════════════════════════════════════════════════════

export const fetchCardsByPatient = createAsyncThunk(
  "cnss/fetchCardsByPatient",
  async (patientId, { rejectWithValue }) => {
    try {
      const path = CNSS_PATHS.BY_PATIENT(patientId);
      logRequest("GET cards by patient", path);
      const { data } = await axiosCnss.get(path);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur");
    }
  }
);

export const fetchDependentsByPatient = createAsyncThunk(
  "cnss/fetchDependentsByPatient",
  async (patientId, { rejectWithValue }) => {
    try {
      const path = CNSS_PATHS.DEPENDENTS_BY_PATIENT(patientId);
      logRequest("GET dependents by patient", path);
      const { data } = await axiosCnss.get(path);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur");
    }
  }
);

export const fetchCardsByCreator = createAsyncThunk(
  "cnss/fetchCardsByCreator",
  async (createdBy, { rejectWithValue }) => {
    try {
      const path = CNSS_PATHS.BY_CREATOR(createdBy);
      logRequest("GET cards by creator", path);
      const { data } = await axiosCnss.get(path);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur");
    }
  }
);

// ════════════════════════════════════════════════════════════════════════════
//  DÉPENDANTS
// ════════════════════════════════════════════════════════════════════════════

export const addDependent = createAsyncThunk(
  "cnss/addDependent",
  async ({ cardId, userId, dependentData }, { rejectWithValue }) => {
    try {
      const path = CNSS_PATHS.ADD_DEPENDENT(cardId, userId);


      const { data } = await axiosCnss.post(path, dependentData);

      return data;
    } catch (err) {
      console.error("[CNSS THUNK] addDependent ERROR status:", err?.response?.status);
      console.error("[CNSS THUNK] addDependent ERROR data:", err?.response?.data);

      return rejectWithValue(err?.response?.data ?? { message: "Erreur ajout dépendant" });
    }
  }
);

export const fetchDependentsByCard = createAsyncThunk(
  "cnss/fetchDependentsByCard",
  async ({ cardId, userId }, { rejectWithValue }) => {
    try {
      const path = `/${encodeURIComponent(String(cardId))}/users/${encodeURIComponent(String(userId))}/dependents`;


      const { data } = await axiosCnss.get(path);

      return data;
    } catch (err) {
      console.error("[CNSS THUNK] fetchDependentsByCard ERROR:");
      console.error("  status:", err?.response?.status);
      console.error("  data  :", err?.response?.data);

      return rejectWithValue(
        err?.response?.data ?? { message: err?.message ?? "Erreur chargement dépendants" }
      );
    }
  }
);



export const fetchDependentById = createAsyncThunk(
  "cnss/fetchDependentById",
  async ({ cardId, userId, dependentId }, { rejectWithValue }) => {
    try {
      const path = CNSS_PATHS.DEPENDENT_BY_ID(cardId, userId, dependentId);
      logRequest("GET dependent by id", path);
      const { data } = await axiosCnss.get(path);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur");
    }
  }
);

export const updateDependent = createAsyncThunk(
  "cnss/updateDependent",
  async ({ cardId, userId, dependentId, updateData }, { rejectWithValue }) => {
    try {
      const path = CNSS_PATHS.UPDATE_DEPENDENT(cardId, userId, dependentId);
      logRequest("PUT updateDependent", path);
      const { data } = await axiosCnss.put(path, updateData);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur mise à jour dépendant");
    }
  }
);

export const deleteDependent = createAsyncThunk(
  "cnss/deleteDependent",
  async ({ cardId, userId, dependentId }, { rejectWithValue }) => {
    try {
      const path = `/${encodeURIComponent(String(cardId))}/users/${encodeURIComponent(String(userId))}/dependents/${encodeURIComponent(String(dependentId))}`;


      await axiosCnss.delete(path);
      return { cardId, dependentId };
    } catch (err) {
      console.error("[CNSS THUNK] deleteDependent ERROR:");
      console.error("  status:", err?.response?.status);
      console.error("  data  :", err?.response?.data);

      return rejectWithValue(
        err?.response?.data ?? { message: err?.message ?? "Erreur suppression dépendant" }
      );
    }
  }
);

// ════════════════════════════════════════════════════════════════════════════
//  ACTES MÉDICAUX
// ════════════════════════════════════════════════════════════════════════════

export const createMedicalAct = createAsyncThunk(
  "cnss/createMedicalAct",
  async ({ doctorId, patientId, actData }, { rejectWithValue }) => {
    try {
      const path = CNSS_PATHS.CREATE_MEDICAL_ACT(doctorId, patientId);
      logRequest("POST createMedicalAct", path);
      const { data } = await axiosCnss.post(path, actData);
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur création acte");
    }
  }
);

export const fetchActsByPatient = createAsyncThunk(
  "cnss/fetchActsByPatient",
  async ({ patientId, type = null }, { rejectWithValue }) => {
    try {
      logRequest("GET acts by patient", `/patients/${patientId}/acts`);
      const { data } = await axiosCnss.get(
        `/patients/${encodeURIComponent(String(patientId))}/acts`,
        { params: type ? { type } : {} }
      );
      return data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur chargement actes");
    }
  }
);

export const deleteMedicalAct = createAsyncThunk(
  "cnss/deleteMedicalAct",
  async (actId, { rejectWithValue }) => {
    try {
      const path = CNSS_PATHS.DELETE_ACT(actId);
      logRequest("DELETE act", path);
      await axiosCnss.delete(path);
      return actId;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message ?? "Erreur suppression acte");
    }
  }
);